import {Injectable, Optional} from '@angular/core';
import {
  ActivatedRouteSnapshot, CanActivate,
  Route, Router, RouterStateSnapshot
} from '@angular/router';
import {Observable} from 'rxjs';
import {LayoutService} from '@ngmedax/layout';
import {LoginService} from '../service/login.service';
import {RegistryService} from '@ngmedax/registry';
import {ConfigService} from '@ngmedax/config';
import {Translatable, TranslationService} from '@ngmedax/translation';
import {KEYS} from '../../translation-keys';
import {TRANSLATION_LOGIN_SCOPE} from '../../constants';

// hack to inject decorator declarations. must occur before class declaration!
export interface AuthenticationGuard extends Translatable {}

@Injectable()
@Translatable({scope: TRANSLATION_LOGIN_SCOPE, keys: KEYS})
export class AuthenticationGuard implements CanActivate {
  /**
   * List of all routes
   */
  private routeList: {[url: string]: Route} = {};

  /**
   * Injects dependencies
   */
  constructor(
    private router: Router,
    private loginService: LoginService,
    private layoutService: LayoutService,
    private configService: ConfigService,
    private registryService: RegistryService,
    @Optional() private translationService: TranslationService) {
    for (const routeConfig of router.config) {
      this.routeList[routeConfig.path] = routeConfig;
    }
  }

  /**
   * Redirects to login if current route requires
   * authentication and user is not logged in.
   *
   * @param {ActivatedRouteSnapshot} route
   * @param {RouterStateSnapshot} state
   * @returns {Observable<boolean> | boolean}
   */
  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean> {
      // by default one must be logged in for every page
      let needsLogin = true;

      const url = '/' + route.url.join('/');

      if (route.params && route.params.authToken) {
        try {
          await this.loginService.logout();
          await this.loginService.tokenLogin(route.params.authToken);
        } catch (error) {
          console.log('token login failed for token:', route.params.authToken);
          console.error(error);
        }
      }

      // get data of current route
      const routeData = route.data ? route.data : this.getRouteData(url);

      // if route data contains hideForMode, check if one of the modes is active and hide if so
      if (routeData['hideForMode']) {
        const hideForMode = routeData['hideForMode'];
        if (Array.isArray(hideForMode)) {
          for (const mode of hideForMode) {
            if (this.configService.get(`mode.${mode}`)) {
              // hide this route
              return false;
            }
          }
        }
      }

      // if route is configured to not need a logged in user
      if (typeof routeData['needsLogin'] !== 'undefined'
        && routeData['needsLogin'] === false) {
        // allow this page
        needsLogin = false;
      }

      // check if user is logged in
      const isLoggedIn = this.loginService.isLoggedIn();

      // if user is not logged in and page needs a logged in user
      if (!isLoggedIn && needsLogin) {
        // set current url as previous. we will use this to redirect when login is done
        this.loginService.setPreviousUri(url);

        // navigate to login page
        this.router.navigate(['module', 'login']).then().catch();
        return false;
      }

      const pwChangeUrl = '/module/ums/user/change';
      const forcePwChange = !!this.registryService.get('forcePwChange');

      if (isLoggedIn && forcePwChange && url != pwChangeUrl) {
        // hide menu > no action allowed until pw is changed
        this.layoutService.hideMenu();

        // navigate to ums change password page
        this.router.navigate(['module', 'ums', 'user', 'change'], { fragment: 'password' }).then().catch();

        setTimeout(() => {
          alert(this._(KEYS.LOGIN.INITIAL_PW_CHANGE_MSG));
        }, 600);

        return false;
      }

      // logout user on "logout" route
      if (url === '/module/login/out') {
        // logout user
        this.loginService.logout().then().catch();
        return true;
      }

      // get user object by login service
      const userObject = this.loginService.getUser();

      // if we found a user object
      if (userObject) {
        // set the username in the layout service (renders it in the user box)
        // TODO: somehow get user image
        this.layoutService.setUser({
          username: userObject.getUsername()
        });
      }

      return true;
  }

  /**
   * Returns data of current route. Returns empty object if no route data found
   *
   * @returns {any}
   */
  private getRouteData(url) {
    url = url.replace(/^\//, '');
    const route = this.routeList[url];
    return (route && route.data) ? route : {};
  }
}
